import { Gray700, Mint700, Red600, Violet600 } from '@zep-us/zus/tokens';

/**
 * 클래스 썸네일(아바타 원) 색과 클래스 배경 참조. LMS 클래스 셀렉터도 같은 색을 보여야 해서
 * 공용에 둔다. 클래스 배경 id 체계는 아래 두 색 표의 키가 정한다.
 */

/** 일러스트 27종의 썸네일 색. 시안(2654:226837) 실측값이며 5색 팔레트를 테마 그룹끼리 공유한다. */
export const CLASSROOM_HERO_AVATAR_COLORS = {
  SPORTS_DAY: '#00ba4a',
  CHRISTMAS: '#5793f9',
  AUTUMN_LEAVES: '#ff8d24',
  HANOK: '#ff8d24',
  TRAIN_STATION: '#ff8d24',
  LIBRARY: '#ff8d24',
  CLASSROOM_DESK: '#ff8d24',
  OX_QUIZ: '#5793f9',
  GREEN_FIELD: '#00ba4a',
  CHALKBOARD: '#00ba4a',
  STADIUM: '#00ba4a',
  TEMPLE: '#00ba4a',
  SCHOOL_HALLWAY: '#5793f9',
  SUMMER_BEACH: '#5793f9',
  SKY_ROOFTOP: '#5793f9',
  WINTER_SNOW: '#5793f9',
  CLASSROOM_GROUP: '#ff7bb7',
  GYM: '#ff8d24',
  SEOUL_NIGHT: '#b179d2',
  MYSTIC_FOREST: '#b179d2',
  MUSIC: '#b179d2',
  SPACE: '#b179d2',
  HALLOWEEN: '#b179d2',
  BIRTHDAY_PARTY: '#ff7bb7',
  BALLOON_HILL: '#ff7bb7',
  CHERRY_BLOSSOM: '#ff7bb7',
  GALLERY: '#5793f9',
} as const;

/**
 * 단색 9종의 썸네일 색. 같은 계열이면 일러스트 5색을 그대로 쓰고, 나머지는 보드 단색 CTA 색
 * (흰 글자 대비를 맞춘 값)을 쓴다.
 */
export const CLASSROOM_SOLID_AVATAR_COLORS = {
  RED: Red600,
  YELLOW: '#ff8d24',
  MINT: Mint700,
  GREEN: '#00ba4a',
  BLUE: '#5793f9',
  VIOLET: Violet600,
  PURPLE: '#b179d2',
  PINK: '#ff7bb7',
  GRAY: Gray700,
} as const;

/** 클래스 아바타 글자색 — 진한 단색 위에 흰 글자로 고정이다. */
export const CLASSROOM_AVATAR_TEXT_COLOR = '#FFFFFF';

export type ClassroomIllustBackgroundId =
  keyof typeof CLASSROOM_HERO_AVATAR_COLORS;
export type ClassroomSolidBackgroundId =
  keyof typeof CLASSROOM_SOLID_AVATAR_COLORS;

/** 서버에 저장하는 클래스 배경 참조. type 과 id 의 짝이 어긋나면 컴파일이 막힌다. */
export type ClassroomBackgroundRef =
  | { type: 'SOLID'; id: ClassroomSolidBackgroundId }
  | { type: 'ILLUST'; id: ClassroomIllustBackgroundId };

/*
  ⚠️ 동결 목록 — 항목 추가·삭제·순서 변경 시 배경을 고르지 않은 기존 학급 전부의 기본 배경이
  바뀐다(학급 id 해시 % 목록 길이). 기본 배경을 일부러 바꿀 때만 고친다.
*/
const CLASSROOM_DEFAULT_ILLUST_IDS = [
  'CLASSROOM_DESK',
  'CLASSROOM_GROUP',
  'CHALKBOARD',
  'SCHOOL_HALLWAY',
  'LIBRARY',
  'GYM',
  'GALLERY',
  'SKY_ROOFTOP',
  'OX_QUIZ',
  'SPORTS_DAY',
  'BALLOON_HILL',
  'BIRTHDAY_PARTY',
  'CHERRY_BLOSSOM',
  'GREEN_FIELD',
  'SUMMER_BEACH',
  'AUTUMN_LEAVES',
  'WINTER_SNOW',
  'CHRISTMAS',
  'HANOK',
  'TEMPLE',
  'TRAIN_STATION',
  'SEOUL_NIGHT',
  'MUSIC',
  'STADIUM',
  'HALLOWEEN',
  'MYSTIC_FOREST',
  'SPACE',
] as const satisfies readonly ClassroomIllustBackgroundId[];

const isIllustId = (id: string): id is ClassroomIllustBackgroundId =>
  Object.prototype.hasOwnProperty.call(CLASSROOM_HERO_AVATAR_COLORS, id);
const isSolidId = (id: string): id is ClassroomSolidBackgroundId =>
  Object.prototype.hasOwnProperty.call(CLASSROOM_SOLID_AVATAR_COLORS, id);

/**
 * 서버가 저장한 클래스 배경(`{ type, id }`)을 참조로 바꾼다.
 * 모르는 id(구버전 JS·내린 배경)나 type 불일치는 null — 학급 id 기본 배경으로 떨어진다.
 */
export function toClassroomBackgroundRef(
  raw?: { type: string; id: string } | null,
): ClassroomBackgroundRef | null {
  if (!raw) return null;
  if (raw.type === 'ILLUST' && isIllustId(raw.id)) {
    return { type: 'ILLUST', id: raw.id };
  }
  if (raw.type === 'SOLID' && isSolidId(raw.id)) {
    return { type: 'SOLID', id: raw.id };
  }
  return null;
}

/** 같은 seed 면 언제나 같은 항목을 고른다(해시 % 길이). 빈 seed 는 'default'. */
function pickBySeed<T>(seed: string | undefined, list: readonly T[]): T {
  const normalizedSeed = seed?.trim() || 'default';
  let hash = 0;
  for (let i = 0; i < normalizedSeed.length; i += 1) {
    hash = (hash << 5) - hash + normalizedSeed.charCodeAt(i);
    hash |= 0;
  }
  return list[Math.abs(hash) % list.length];
}

/** 배경을 고르지 않은 학급의 기본 배경. 학급 id 를 시드로 일러스트를 고르며 저장하지 않는다. */
export function getDefaultClassroomBackgroundRef(
  seed?: string,
): ClassroomBackgroundRef {
  return { type: 'ILLUST', id: pickBySeed(seed, CLASSROOM_DEFAULT_ILLUST_IDS) };
}

/**
 * 배경 참조 → 썸네일 색. 기본값은 호출부가
 * `toClassroomBackgroundRef(saved) ?? getDefaultClassroomBackgroundRef(id)` 로 정해 넘긴다.
 */
export function getClassroomAvatarColor(ref: ClassroomBackgroundRef): {
  avatarColor: string;
  avatarTextColor: string;
} {
  return {
    avatarColor:
      ref.type === 'SOLID'
        ? CLASSROOM_SOLID_AVATAR_COLORS[ref.id]
        : CLASSROOM_HERO_AVATAR_COLORS[ref.id],
    avatarTextColor: CLASSROOM_AVATAR_TEXT_COLOR,
  };
}
